/**
 * Shared pricing assumptions used by every closing-cost calculator, so a
 * change here moves every estimate together.
 */

/**
 * Annual homeowners insurance as a share of the purchase price when the
 * property has no quote of its own. 0.65% is about $2,400 a year on a
 * $375,000 home, in line with the national average premium.
 * Keep in step with INSURANCE_RATE_ANNUAL in the backend
 * (services/userInterestRateService.js).
 */
export const HOMEOWNERS_INSURANCE_RATE = 0.0065;

/** FHA upfront mortgage insurance premium, percent of the base loan. */
export const FHA_UPFRONT_MIP_PERCENT = 1.75;

/** FHA loan limit that splits the annual MIP tiers (HUD ML 2023-05). */
const FHA_MIP_LOAN_LIMIT = 726_200;

/**
 * FHA annual mortgage insurance premium rate (decimal, e.g. 0.0055) per HUD
 * Mortgagee Letter 2023-05, effective March 20, 2023.
 *
 * @param baseLoanAmount base loan, before the upfront premium is financed
 * @param ltvPercent loan-to-value as a percent (e.g. 96.5)
 * @param termYears loan term in years
 */
export function fhaAnnualMipRate(
  baseLoanAmount: number,
  ltvPercent: number,
  termYears = 30
): number {
  const highBalance = baseLoanAmount > FHA_MIP_LOAN_LIMIT;
  if (termYears > 15) {
    if (highBalance) return ltvPercent <= 95 ? 0.007 : 0.0075;
    return ltvPercent <= 95 ? 0.005 : 0.0055;
  }
  if (highBalance) {
    if (ltvPercent <= 78) return 0.0015;
    if (ltvPercent <= 90) return 0.004;
    return 0.0065;
  }
  return ltvPercent <= 90 ? 0.0015 : 0.004;
}
